import { useEffect, useLayoutEffect, useState } from 'react';
import MeetingUploadFile from '@/components/MeetingUploadFile';
import Split from 'react-split';

import './meeting.scss';
import MeetingBoard from '@/components/MeetingBoard';
import { clearToast, showToast } from '@/components/ToastManager';
import ToolBarPanel from '@/components/ToolBarPanel';
import Api from '@/api/api.ts';

import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useLocalParticipant
} from '@livekit/components-react';
import { LocalAudioTrack } from 'livekit-client';
import { ParticipantRole } from '@/consts/meeting.ts';
import MeetingSider from '@/components/MeetingSider';
import { useLocation, useNavigate, useParams } from 'react-router';

import { SERVER_URL } from '@/consts/meeting';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useUserStore } from '@/stores/user.ts';

import { jwtDecode } from 'jwt-decode';
import StudyModeManager from '@/components/StudyModeManager';
import { SendProvider } from '@/components/SendProvider';
import { useNoiseSuppression } from '@/hooks/useNoiseSuppression';
import { useMeetingStore } from '@/stores/meeting.ts';

import clsx from 'clsx';

function Meeting() {
  const { meetingUid } = useParams();
  const [token, setToken] = useState<string | undefined>(undefined);

  const [isEnterNameModalOpened, setIsEnterNameModalOpened] = useState(false);
  const [assistantToken, setAssistantToken] = useState<string | null>(null);

  const userUid = useUserStore((state) => state.userUid);
  const userName = useUserStore((state) => state.userName);

  const { setRole, setUserUid, setUserName } = useUserStore.getState();
  const { setMeeting, setInitialRooms } = useMeetingStore.getState();

  const { state } = useLocation();
  const navigate = useNavigate();

  const getAndJoinMeeting = async () => {
    if (!meetingUid || !userName) {
      return;
    }

    try {
      const meeting = await Api.getCurrentMeeting(meetingUid);
      if (!meeting) {
        throw new Error(`meeting is ${meeting}`);
      }
      setMeeting(meeting);
    } catch (err) {
      console.error('Error getting meeting', err);
      navigate('/');
      return;
    }

    try {
      const rooms = await Api.getRooms(meetingUid);
      const meetingRooms = rooms.map((room) => ({
        id: room.id,
        title: room.name
      }));
      setInitialRooms(meetingRooms);
    } catch (err) {
      console.error('Error getting rooms of meeting', err);
    }

    let savedUserUid = localStorage.getItem('userId') || '';
    try {
      if (!savedUserUid) {
        savedUserUid = state?.author_uuid || crypto.randomUUID();
        localStorage.setItem('userId', savedUserUid);
        await Api.addParticipant(meetingUid, savedUserUid, userName);
      }
      setUserUid(savedUserUid);
    } catch (error) {
      console.error('Error get userId:', error);
    }

    try {
      const loginResponse = await Api.loginInMeeting({
        meetingUid,
        userUid: savedUserUid,
        assistantInviteToken: state?.assistant_invite_token
      });
      // decode the token to get the payload with the role
      const decodedToken: { role: ParticipantRole } = jwtDecode(loginResponse.access_token);
      setRole(decodedToken.role);

      if (decodedToken.role === ParticipantRole.TEACHER) {
        try {
          const assistantToken = await Api.getLinkTokenAssistant(meetingUid);
          setAssistantToken(assistantToken);
        } catch (error) {
          console.error('Error loading link for assistants', error);
        }
      }
    } catch (error) {
      console.error('Error login in meeting:', error);
    }

    try {
      const newToken = await Api.generateToken({
        meetingUid,
        identity: savedUserUid,
        name: userName
      });
      setToken(newToken);
    } catch (error) {
      console.error('Error getting token to room:', error);
    }
  };

  const handleConnectionError = (error: Error) => {
    console.error('Failed to connect to LiveKit room:', error);
    showToast(
      'connectionErrorToast',
      'error',
      'Ошибка подключения ко встрече',
      <>
        Обратитесь к администратору{' '}
        <a
          href="https://t.me/repeteka_support"
          target="_blank"
          style={{ color: '#0ea5e9' }}
          rel="noreferrer"
        >
          @repeteka_support
        </a>
      </>
    );
  };

  const getName = async (userUid: string) => {
    if (meetingUid) {
      const name = await Api.getNameParticipant(meetingUid, userUid);
      setUserName(name);
    }
  };

  useEffect(() => {
    if (state?.author_name) {
      setUserName(state.author_name);
    } else {
      // not redirected from CreateMeeting, opening modal with user's name input
      if (!userUid) {
        setIsEnterNameModalOpened(true);
      } else {
        void getName(userUid);
      }
    }
  }, []);

  useEffect(() => {
    if (isEnterNameModalOpened) {
      // user hasn't finished typing his name, don't connect
      return;
    }

    void getAndJoinMeeting();
  }, [meetingUid, userName, isEnterNameModalOpened]);

  if (!meetingUid) {
    return;
  }

  return (
    <LiveKitRoom
      audio={{
        noiseSuppression: true,
        echoCancellation: true,
        voiceIsolation: true
      }}
      token={token}
      serverUrl={SERVER_URL}
      onError={handleConnectionError}
    >
      <SendProvider>
        <div className="meeting">
          <MeetingContent assistantToken={assistantToken} />
          <Dialog
            visible={isEnterNameModalOpened}
            resizable={false}
            draggable={false}
            closable={false}
            onHide={() => {}}
            className="custom-dialog"
          >
            <div className="meeting__modal">
              <label className="meeting__modal-heading" htmlFor="userName">
                Введите свое имя, чтобы присоединиться к встрече
              </label>
              <InputText
                className="p-inputtext-sm meeting__modal-input"
                id="userName"
                value={userName}
                placeholder="Ваше имя или никнейм"
                onChange={(e) => setUserName(e.target.value)}
              />
              <Button
                className="meeting__modal-submit"
                icon="pi pi-check"
                iconPos="right"
                size="small"
                disabled={!userName}
                onClick={() => setIsEnterNameModalOpened(false)}
              >
                Готово
              </Button>
            </div>
          </Dialog>
        </div>
      </SendProvider>
    </LiveKitRoom>
  );
}

type RoomContentProps = {
  assistantToken: string | null;
};

function MeetingContent({ assistantToken }: RoomContentProps) {
  const connectionState = useConnectionState();
  const [loading, setLoading] = useState(true);
  const [reconnectLoading, setReconnectLoading] = useState(false);
  const { microphoneTrack } = useLocalParticipant();
  const [sizes, setSizes] = useState([50, 50]);
  const { setNoiseFilterEnabled, isNoiseFilterEnabled, isNoiseFilterPending, isSupported } =
    useNoiseSuppression(microphoneTrack?.audioTrack as LocalAudioTrack);

  const [activeId, setActiveId] = useState<string | null>(null);

  const { role, participantActiveCanvas } = useUserStore();
  const { activeToolPanel } = useMeetingStore();

  const { setActiveToolPanel } = useMeetingStore();

  useLayoutEffect(() => {
    if (role !== ParticipantRole.TEACHER) return;
    setActiveId(participantActiveCanvas);
  }, [participantActiveCanvas]);

  useEffect(() => {
    if (connectionState === 'reconnecting' && !reconnectLoading) {
      setReconnectLoading(true);
      showToast(
        'reconnectingToast',
        'warn',
        'Переподключение...',
        'Соединение с встречей восстанавливается',
        true
      );
    }
    if (connectionState === 'connected') {
      setLoading(false);
      if (reconnectLoading) {
        setReconnectLoading(false);
        clearToast('reconnectingToast');
        showToast(
          'reconnectingSuccessToast',
          'success',
          'Соединение восстановлено',
          'Вы снова на встрече'
        );
      }

      // enable krisp only when connected, the mic track exists, and it's not already on or pending
      // enable noise filter only when connected, the mic track exists, and it's supported and not already on or pending
      if (isSupported && microphoneTrack && !isNoiseFilterEnabled && !isNoiseFilterPending) {
        setNoiseFilterEnabled(true).catch((e) =>
          console.error('Failed to enable noise filter:', e)
        );
      }
    }
  }, [connectionState]);

  return (
    <>
      <ToolBarPanel />
      <Split
        className="custom-split"
        gutterSize={activeToolPanel === '' ? 0 : 6}
        sizes={activeToolPanel === '' ? [0, 100] : [10, 90]}
        minSize={activeToolPanel === '' ? 0 : 10}
        direction="horizontal"
        cursor="col-resize"
        collapsed={activeToolPanel === '' ? 0 : undefined}
      >
        <div
          className={clsx(
            'meeting__left_panel',
            activeToolPanel === '' && 'meeting__left_panel-close'
          )}
        >
          <div
            className={clsx(
              'meeting__left',
              'meeting__left_sider',
              activeToolPanel !== '' && 'meeting__left_sider-open'
            )}
          >
            <StudyModeManager />
            <MeetingSider loading={loading} assistantTokenForInvite={assistantToken} />
            <RoomAudioRenderer />
          </div>
        </div>
        <div className="meeting__right">
          <div
            className={clsx(
              'meeting__right_overlay',
              activeToolPanel !== '' && 'meeting__right_overlay-open'
            )}
            onClick={() => {
              setActiveToolPanel('');
            }}
          />
          <Split
            direction="vertical"
            className="vertical-split"
            gutterSize={6}
            sizes={sizes}
            minSize={0}
            onDragEnd={(newSizes) => setSizes([...newSizes])}
            gutter={() => {
              const g = document.createElement('div');
              g.className = 'custom-gutter';

              g.innerHTML = `
                <div class="gutter-handle"></div>
                <button class="arrow arrow-up"><i class="pi pi-angle-up"></i></button>
                <button class="arrow arrow-down"><i class="pi pi-angle-down"></i></button>
              `;

              const handle = g.querySelector('.gutter-handle');
              const up = g.querySelector('.arrow-up');
              const down = g.querySelector('.arrow-down');

              if (handle) {
                handle.addEventListener('dblclick', (e) => {
                  e.stopPropagation();
                  setSizes([50, 50]);
                });
              }

              const preventDrag = (e: Event) => {
                e.stopPropagation();
              };

              if (up && down) {
                up.addEventListener('mousedown', preventDrag);
                up.addEventListener('touchstart', preventDrag);
                down.addEventListener('mousedown', preventDrag);
                down.addEventListener('touchstart', preventDrag);

                up.addEventListener('click', (e) => {
                  e.stopPropagation();
                  setSizes([0, 100]);
                });

                down.addEventListener('click', (e) => {
                  e.stopPropagation();
                  setSizes([100, 0]);
                });
              }

              return g;
            }}
          >
            <div
              className={
                participantActiveCanvas === 'canvasFile' ? 'pdf-full-height' : 'pdf-zero-height'
              }
              style={{ height: '100%' }}
            >
              {!loading && (
                <MeetingUploadFile id="canvasFile" activeId={activeId} setActiveId={setActiveId} />
              )}
            </div>
            <div className={participantActiveCanvas === 'canvasBoard' ? 'board-full-height' : ''}>
              {!loading && (
                <MeetingBoard id="canvasBoard" activeId={activeId} setActiveId={setActiveId} />
              )}
            </div>
          </Split>
        </div>
      </Split>
      {(loading || reconnectLoading) && (
        <div className="meeting__foreground">
          <div className="meeting__loader">
            <ProgressSpinner className="meeting__custom-spinner" />
          </div>
        </div>
      )}
    </>
  );
}

export default Meeting;
